import {
  type ConnectionOperation,
  DOCUMENT_FIELDS_PATH,
  type EntityOperation,
  type SetFieldsOperation,
} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {useMutation} from '@github-ui/react-query'
import {useCallback} from 'react'

import {
  addCommentOperation,
  deleteCommentOperation,
  type OptimisticConnection,
  updateCommentOperation,
} from './comment-connection-overlay'
import {
  addCommentRequest,
  type CommentRoute,
  deleteIssueCommentRequest,
  type ExistingCommentRoute,
  updateIssueCommentBodyRequest,
} from './comment-mutation-requests'

type MutationCallbacks = {
  onCompleted?: () => void
  onError?: (error: Error) => void
}

type AddCommentResponse = Awaited<ReturnType<typeof addCommentRequest>>
type DeleteCommentResponse = Awaited<ReturnType<typeof deleteIssueCommentRequest>>

type AddMetadata = MutationCallbacks & {
  kind: 'add'
  route: CommentRoute
  body: string
  execute: () => Promise<AddCommentResponse>
}
type DeleteMetadata = MutationCallbacks & {
  kind: 'delete'
  route: ExistingCommentRoute
  nodeId: string
  execute: () => Promise<DeleteCommentResponse>
}
type UpdateMetadata = MutationCallbacks & {
  kind: 'update'
  route: ExistingCommentRoute
  body: string
  bodyVersion: string
}
type CommentMutationMetadata = AddMetadata | DeleteMetadata | UpdateMetadata
type CommentMutationOperation<
  TOperation extends ConnectionOperation = ConnectionOperation,
  TMetadata extends CommentMutationMetadata = CommentMutationMetadata,
> = TOperation & {commentMutation: TMetadata}
type CountMutationVariables = {operation: EntityOperation; operationId: string}

function withMetadata<TOperation extends ConnectionOperation, TMetadata extends CommentMutationMetadata>(
  operation: TOperation,
  commentMutation: TMetadata,
): CommentMutationOperation<TOperation, TMetadata> {
  return {...operation, commentMutation}
}

function metadataFor<TKind extends CommentMutationMetadata['kind']>(
  operation: ConnectionOperation,
  kind: TKind,
): Extract<CommentMutationMetadata, {kind: TKind}> {
  const metadata = (operation as Partial<CommentMutationOperation>).commentMutation
  if (!metadata || metadata.kind !== kind) throw new Error(`Missing ${kind} comment mutation input`)
  return metadata as Extract<CommentMutationMetadata, {kind: TKind}>
}

function asError(error: unknown, fallback: string): Error {
  return error instanceof Error ? error : new Error(fallback)
}

function asCommentNode(node: object): Record<string, unknown> & {id: string} {
  return node as Record<string, unknown> & {id: string}
}

export function useAddCommentMutation({connection: {ownerId, connectionPath}}: {connection: OptimisticConnection}) {
  const mutation = useConnectionMutation<AddCommentResponse>({
    ownerId,
    connectionPath,
    mutationFn: operation => metadataFor(operation, 'add').execute(),
    onSuccess: async (data, variables) => {
      const metadata = metadataFor(variables.operation, 'add')
      const node = data.addComment?.timelineEdge?.node
      if (!node) throw new Error('Add comment mutation returned no comment')

      const temporaryId = variables.operation.type === 'addToConnection' ? variables.operation.node.id : undefined
      const confirmed = addCommentOperation({
        connectionPath,
        node: asCommentNode(node),
        temporaryId,
        fallbackId: ownerId,
      })
      await notifySettlement(confirmed, ownerId, connectionPath)

      metadata.onCompleted?.()
    },
    onError: (error, variables) => {
      const metadata = metadataFor(variables.operation, 'add')
      metadata.onError?.(error)
    },
  })
  const {mutate: mutateCount} = useMutation<AddCommentResponse, Error, CountMutationVariables>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: ({operation}) => metadataFor(operation, 'add').execute(),
    onSuccess: async (_data, variables) => {
      await notifySettlement(variables.operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH, variables.operationId)
    },
  })

  return useCallback(
    ({
      route,
      body,
      optimisticNode,
      onCompleted,
      onError,
    }: MutationCallbacks & {
      route: CommentRoute
      body: string
      optimisticNode?: (Record<string, unknown> & {id?: string | null}) | null
    }) => {
      let request: Promise<AddCommentResponse> | undefined
      const execute = () => (request ??= addCommentRequest(route, {body}))
      const metadata: AddMetadata = {kind: 'add', route, body, execute, onCompleted, onError}
      const operation = withMetadata(
        addCommentOperation({
          connectionPath,
          node: optimisticNode,
          temporaryId: optimisticNode?.id ?? undefined,
          fallbackId: ownerId,
        }),
        metadata,
      )
      const countOperation = withMetadata<EntityOperation, AddMetadata>(
        {type: 'deltaEntityField', id: ownerId, field: 'comments', delta: 1, clamp: {min: 0}},
        metadata,
      )

      try {
        mutation.mutate({operation})
        mutateCount({operation: countOperation, operationId: crypto.randomUUID()})
      } catch (error) {
        const mutationError = asError(error, 'Failed to add comment')
        onError?.(mutationError)
      }
    },
    [connectionPath, mutateCount, mutation, ownerId],
  )
}

export function useDeleteIssueCommentMutation({
  connection: {ownerId, connectionPath},
}: {
  connection: OptimisticConnection
}) {
  const mutation = useConnectionMutation<DeleteCommentResponse>({
    ownerId,
    connectionPath,
    mutationFn: operation => metadataFor(operation, 'delete').execute(),
    onSuccess: async (data, variables) => {
      const metadata = metadataFor(variables.operation, 'delete')
      const commentId = data.deleteIssueComment?.issueComment?.id
      if (!commentId) throw new Error('Delete comment mutation returned no comment')
      await notifySettlement(variables.operation, ownerId, connectionPath)

      metadata.onCompleted?.()
    },
    onError: (error, variables) => {
      const metadata = metadataFor(variables.operation, 'delete')
      metadata.onError?.(error)
    },
  })
  const {mutate: mutateCount} = useMutation<DeleteCommentResponse, Error, CountMutationVariables>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: ({operation}) => metadataFor(operation, 'delete').execute(),
    onSuccess: async (_data, variables) => {
      await notifySettlement(variables.operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH, variables.operationId)
    },
  })

  return useCallback(
    ({route, nodeId, onCompleted, onError}: MutationCallbacks & {route: ExistingCommentRoute; nodeId: string}) => {
      let request: Promise<DeleteCommentResponse> | undefined
      const execute = () => (request ??= deleteIssueCommentRequest(route))
      const metadata: DeleteMetadata = {
        kind: 'delete',
        route,
        nodeId,
        execute,
        onCompleted,
        onError,
      }
      const operation = withMetadata(deleteCommentOperation(connectionPath, nodeId), metadata)
      const countOperation = withMetadata<EntityOperation, DeleteMetadata>(
        {type: 'deltaEntityField', id: ownerId, field: 'comments', delta: -1, clamp: {min: 0}},
        metadata,
      )

      try {
        mutation.mutate({operation})
        mutateCount({operation: countOperation, operationId: crypto.randomUUID()})
      } catch (error) {
        const mutationError = asError(error, 'Failed to delete comment')
        onError?.(mutationError)
      }
    },
    [connectionPath, mutateCount, mutation, ownerId],
  )
}

export type UpdateIssueCommentBodyMutation = (
  input: MutationCallbacks & {
    route: ExistingCommentRoute
    nodeId: string
    body: string
    bodyVersion: string
  },
) => void

type UpdateIssueCommentBodyResponse = Awaited<ReturnType<typeof updateIssueCommentBodyRequest>>

function confirmedUpdateCommentOperation(data: UpdateIssueCommentBodyResponse): SetFieldsOperation {
  const comment = data.updateIssueComment?.issueComment
  if (!comment) throw new Error('Update comment mutation returned no comment')
  return {
    type: 'setFields',
    id: comment.id,
    fields: {
      body: comment.body,
      bodyHTML: comment.bodyHTML,
      bodyVersion: comment.bodyVersion,
      viewerCanUpdate: comment.viewerCanUpdate,
    },
  }
}

export function useUpdateIssueCommentBodyMutation(): UpdateIssueCommentBodyMutation {
  const mutation = useEntityMutation<UpdateIssueCommentBodyResponse>({
    mutationFn: operation => {
      const {route, body, bodyVersion} = metadataFor(operation, 'update')
      return updateIssueCommentBodyRequest(route, {body, bodyVersion})
    },
    getConfirmedOperation: confirmedUpdateCommentOperation,
    onSuccess: (_data, variables) => {
      const metadata = metadataFor(variables.operation, 'update')
      metadata.onCompleted?.()
    },
    onError: (error, variables) => {
      metadataFor(variables.operation, 'update').onError?.(error)
    },
  })

  return useCallback(
    ({
      route,
      nodeId,
      body,
      bodyVersion,
      onCompleted,
      onError,
    }: MutationCallbacks & {
      route: ExistingCommentRoute
      nodeId: string
      body: string
      bodyVersion: string
    }) => {
      const operation = withMetadata(updateCommentOperation(nodeId, body, bodyVersion), {
        kind: 'update',
        route,
        body,
        bodyVersion,
        onCompleted,
        onError,
      })
      try {
        mutation.mutate({operation})
      } catch (error) {
        onError?.(asError(error, 'Failed to update comment'))
      }
    },
    [mutation],
  )
}
